import ResultImage from 'components/ui/Results/ResultImage';
import { ResultItemProps } from 'components/ui/Results/ResultItem';
import React, { useEffect, useState } from 'react';
// #region HCL
import { Localizer } from 'services/Localizer';
import { getUserData } from 'services/UserData';
import {
	AmbiguousDisplayPrice,
	DisplayPrice,
	PriceDisplayRule,
	SimpleDisplayPrice,
} from 'shared-client/types/commerce/display-price';
// #endregion
import ProductPricing from 'shared/components/commerce/ProductPricing';
import { useHawkSearch } from 'shared/components/hawksearch/components/StoreProvider';
import { dispatchRocEvent } from 'shared/hooks/useRocEventHandler';
// #region HCL
import ProductAddToCart from './ProductAddToCart';
import Spinner from 'shared/components/Spinner';
// #endregion

// #region HCL - added productPricingAndAvailability
function ProductResultItem({ item, testId, productPricingAndAvailability, isLoadingPricing }: ResultItemProps) {
	// #endregion
	const [compareCheck, setCompareCheck] = useState(false);
	// #region HCL
	const [userIsSignedIn, setUserIsSignedIn] = useState<boolean>(false);
	const guestQuoteEnabled = item.getDocumentValue('product_guestquoteenabled') === 'true';
	// #endregion
	const {
		store: { itemsToCompareIds /*#region HCL*/, searchResults /*#endregion*/ },
		actor,
	} = useHawkSearch();
	useEffect(() => {
		setCompareCheck(itemsToCompareIds.indexOf(item.DocId) !== -1 ? true : false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [itemsToCompareIds]);
	// #region HCL
	useEffect(() => {
		getUserData().then((res) => {
			setUserIsSignedIn(res?.data?.summary?.isSignedIn ?? false);
		});
	}, []);
	// #endregion

	const itemName = item.getDocumentValue('name');
	const url = item.getDocumentValue('url_detail');

	// unique_id is required for HawkSearch tracking
	const uniqueId = item.getDocumentValue('unique_id');

	// function for dispatching product-clicked event
	// this is handled by hawksearch-commerce-tracker to report click event to Hawk Tracking API
	const dispatchProductClickEvent = () => {
		if (uniqueId) {
			dispatchRocEvent('product-clicked', {
				productId: uniqueId,
				url: url || '',
				productName: itemName || '',
				productGroupId: null,
			});
		}
	};

	function toggleItemsToCompare() {
		if (itemsToCompareIds.length >= 5 && compareCheck === false) {
			alert('You can compare up to 5 items');
			return;
		}
		actor.setItemsToCompare(item, !compareCheck);
	}

	// #region HCL
	const GetSalePercentageSummary = () => {
		const displayPriceStringified =
			item.getDocumentValue('product_groupdisplayprice') || item.getDocumentValue('product_displayprice');

		const displayPrice = displayPriceStringified
			? (JSON.parse(displayPriceStringified) as DisplayPrice)
			: undefined;

		if (!displayPrice) {
			return;
		}

		let discount: number = 0;
		switch (displayPrice.displayRule) {
			case PriceDisplayRule.Normal:
				discount = getSimpleDiscount(displayPrice);
				break;

			case PriceDisplayRule.AmbiguousRange:
				discount = getAmbiguousDiscount(displayPrice);
				break;
		}

		return discount;
	};

	const getSimpleDiscount = (price: SimpleDisplayPrice) => {
		let discount: number = 0;
		if (price.salePrice?.rawValue) {
			const basePrice = Number(price.basePrice.rawValue);
			const salePrice = Number(price.salePrice.rawValue);

			discount = 100 - (100 * salePrice) / basePrice;
		}
		return Math.round(discount);
	};

	const getAmbiguousDiscount = (price: AmbiguousDisplayPrice) => {
		let discount: number = 0;
		if (price.salePrice?.rawValue) {
			const basePrice = Number(price.basePrice.rawValue);
			const salePrice = Number(price.salePrice.rawValue);

			discount = 100 - (100 * salePrice) / basePrice;
		}
		return Math.round(discount);
	};

	const isNewProduct = () => {
		const categories = item.getDocumentValues('category');
		if (!categories) {
			return false;
		}
		const category = categories.find((c) => c.includes('New Products'));
		if (!category) {
			return false;
		}
		return true;
	};

	/**
	 * Whether this item has child skus, or this item has multiple SKUs, which is only possible for products with child items
	 */
	const hasChildSkus = () => {
		const childSkus = item.getDocumentValues('product_sku_child');
		if (childSkus) {
			return true;
		}
		const skus = item.getDocumentValues('product_sku');
		if (skus && skus.length > 1) {
			return true;
		}
		return false;
	};

	const discount = GetSalePercentageSummary() ?? 0;
	const sku = item.getDocumentValue('product_sku');
	const search = searchResults?.Keyword !== '' ? '?keyword=' + searchResults?.Keyword : '';
	// #endregion

	return (
		<div className="hawk-results__item">
			{/* #region HCL */}
			{discount > 0 ? (
				<div className="roc-pdp-assets__product-sale">
					<span className="roc-pdp-assets__product-sale-label">{Localizer('Sale')}</span>
					<span className="roc-pdp-assets__product-sale-percentage">{discount}%</span>
				</div>
			) : null}

			{isNewProduct() ? (
				<div className="roc-pdp-assets__product-new">
					<span className="roc-pdp-assets__product-label-new">{Localizer('New')}</span>
				</div>
			) : null}
			{/* #endregion */}

			<ResultImage
				item={item}
				onClick={dispatchProductClickEvent}
				/*#region HCL */ search={search} /*endregion*/
			/>

			<div className="hawk-results__item-name" data-testid={`hawk-result-${testId}`}>
				<div className="hawk-results__product-detail">
					{url ? (
						<a
							className="hawk-results__item-name-link"
							href={url /*#region HCL */ + search /*endregion*/}
							onClick={() => dispatchProductClickEvent()}
						>
							<span>{itemName}</span>
						</a>
					) : (
						<span>{itemName}</span>
					)}
					{/* #region HCL */}
					{sku && !hasChildSkus() ? (
						<p>
							<span>{Localizer('ItemNo') + ': '}</span>
							<span>{sku}</span>
						</p>
					) : null}
					{/* #endregion */}
				</div>
				<div className="hawk-results__price-and-cart">
					{/* #region HCL */}
					<ProductPriceRenderer
						item={item}
						testId={testId}
						productPricingAndAvailability={productPricingAndAvailability}
						isLoadingPricing={isLoadingPricing}
						guestQuoteEnabled={guestQuoteEnabled}
						userIsSignedIn={userIsSignedIn}
					/>
					{/* #endregion */}
					<div className="hawk-results__actions-wrapper">
						<div className="hawk-compare">
							<label className="hawk-compare__label" htmlFor={uniqueId}>
								<input
									className="hawk-compare__checkbox"
									id={uniqueId}
									type="checkbox"
									checked={compareCheck}
									onChange={() => toggleItemsToCompare()}
								/>
								<span className="hawk-compare__text">{Localizer('Compare')}</span>
							</label>
						</div>
						{/* #region HCL */}
						{!item.getDocumentValue('product_groupdisplayprice') ? (
							<ProductAddToCart
								item={item}
								productPricingAndAvailability={productPricingAndAvailability}
							/>
						) : null}
						{/* #endregion */}
					</div>
				</div>
			</div>
		</div>
	);
}

// #region HCL - added productPricingAndAvailability & isLoadingPricing & guestQuoteEnabled & userIsSignedIn
function ProductPriceRenderer({
	item,
	testId,
	productPricingAndAvailability,
	isLoadingPricing,
	guestQuoteEnabled,
	userIsSignedIn,
}: ResultItemProps & { guestQuoteEnabled?: boolean; userIsSignedIn?: boolean }) {
	const displayPriceStringified =
		productPricingAndAvailability?.price ||
		item.getDocumentValue('product_groupdisplayprice') ||
		item.getDocumentValue('product_displayprice');
	// endregion
	const displayPrice = displayPriceStringified ? (JSON.parse(displayPriceStringified) as DisplayPrice) : undefined;
	// #region HCL
	const hidePriceForGuestQuote = guestQuoteEnabled === true && !userIsSignedIn;
	// #endregion HCL
	return (
		<>
			{/* #region HCL  */}
			{hidePriceForGuestQuote ? null : isLoadingPricing ? (
				<Spinner />
			) : (
				// #endregion
				<ProductPricing
					className="hawk-results__item-price"
					displayPrice={displayPrice}
					testId={`hawk-result-pricing-${testId}`}
					roundValue={true}
				/>
				// #region HCL
			)}
			{/* #endregion */}
		</>
	);
}

export default ProductResultItem;
